import { Injectable, inject } from '@angular/core';
import { UntypedFormArray } from '@angular/forms';

import { UserService } from './user.service';
import { UserAdminContractParserService } from './user-admin-contract-parser.service';

import {
  USER_ADMIN_FORM_VALUES,
  USER_ADMIN_GMA,
} from '../constants/user-admin-contract.constants';

@Injectable({
  providedIn: 'root',
})
export class UserAdminContractFormService {
  private readonly userService = inject(UserService);
  private readonly parser = inject(UserAdminContractParserService);

  get contactRows(): UntypedFormArray {
    return this.userService.contactInfoFormGroup.get(
      'contacts'
    ) as UntypedFormArray;
  }

  get gmaRows(): UntypedFormArray {
    return this.userService.regionFormGroup.get('gmaList') as UntypedFormArray;
  }

  addContactRow(): void {
    this.contactRows.push(this.userService.createContactRow());
  }

  removeContactRow(index: number): void {
    if (this.contactRows.length > 1) {
      this.contactRows.removeAt(index);

      return;
    }

    this.contactRows.at(0).reset(
      {
        id: crypto.randomUUID(),
        name: '',
        email: '',
        phone: '',
      },
      { emitEvent: false }
    );
  }

  addGmaRow(): void {
    this.gmaRows.push(this.userService.createGmaRow());
  }

  removeGmaRow(index: number): void {
    if (this.gmaRows.length > 1) {
      this.gmaRows.removeAt(index);

      return;
    }

    this.gmaRows.at(0).reset({
      id: crypto.randomUUID(),
      gmaCoordinates: '',
      gmaManufacturer: '',
      gmaWebcam: USER_ADMIN_GMA.defaultWebcamValue,
      gmaProductKey: '',
    });

    this.userService.regionFormGroup
      .get('hasGma')
      ?.setValue(USER_ADMIN_FORM_VALUES.no);
  }

  patchEmptyRegionInfo(): void {
    this.userService.regionFormGroup.patchValue(
      {
        locations: '',
        localFeatures: '',
        elevationMin: null,
        elevationMax: null,
        bridges: USER_ADMIN_FORM_VALUES.few,
        hasGma: USER_ADMIN_FORM_VALUES.no,
      },
      { emitEvent: false }
    );

    this.gmaRows.clear({ emitEvent: false });
    this.gmaRows.push(this.userService.createGmaRow(), {
      emitEvent: false,
    });
  }

  patchRegionInfoFromText(text: string, isEditing: boolean): void {
    const regionInfo = this.parser.parseRegionInfo(text);

    this.userService.regionFormGroup.patchValue(
      {
        locations: regionInfo.locations,
        localFeatures: regionInfo.localFeatures,
        elevationMin: regionInfo.elevationMin,
        elevationMax: regionInfo.elevationMax,
        bridges: regionInfo.bridges,
        hasGma: regionInfo.hasGma,
      },
      { emitEvent: false }
    );

    if (isEditing) {
      return;
    }

    this.gmaRows.clear({ emitEvent: false });

    if (regionInfo.hasGmaDetails) {
      this.patchGmaRowsFromText(regionInfo.gmaDetailsPart);
    }

    this.ensureAtLeastOneGmaRow();
  }

  patchContactInfoFromText(text: string): void {
    const contactInfo = this.parser.parseContactInfo(text);

    this.contactRows.clear({ emitEvent: false });
    this.patchContactRows(contactInfo.contactRows);

    this.userService.contactInfoFormGroup.patchValue(
      {
        billingCompanyName: contactInfo.billingAddress.company,
        billingStreet: contactInfo.billingAddress.street,
        billingZip: contactInfo.billingAddress.zip,
        billingCity: contactInfo.billingAddress.city,
        billingEmail: contactInfo.billingEmail,
      },
      { emitEvent: false }
    );
  }

  private patchGmaRowsFromText(gmaDetailsPart: string): void {
    const parsedGmaRows = this.parser.parseGmaRows(gmaDetailsPart);

    parsedGmaRows.forEach(gmaRow => {
      this.gmaRows.push(
        this.userService.createGmaRow(
          gmaRow.coordinates,
          gmaRow.manufacturer,
          gmaRow.webcam,
          gmaRow.productKey
        ),
        { emitEvent: false }
      );
    });
  }

  private ensureAtLeastOneGmaRow(): void {
    if (this.gmaRows.length > 0) {
      return;
    }

    this.gmaRows.push(this.userService.createGmaRow(), {
      emitEvent: false,
    });
  }

  private patchContactRows(
    contactRows: { name: string; email: string; phone: string }[]
  ): void {
    if (contactRows.length === 0) {
      this.contactRows.push(this.userService.createContactRow(), {
        emitEvent: false,
      });

      return;
    }

    contactRows.forEach(contactRow => {
      this.contactRows.push(
        this.userService.createContactRow(
          contactRow.name,
          contactRow.email,
          contactRow.phone
        ),
        { emitEvent: false }
      );
    });
  }
}
